import {
  Avatar,
  Box,
  FlexBox,
  Page,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { ampli } from 'lib-shared-tracking';
import { useCallback } from 'react';

import { appActions } from 'app/app.actions';
import { categories } from 'utils/elevio';
import { featureFlags } from 'utils/featureFlagging/featureFlags';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { getTooltipContent } from '../../utils/integrationTooltipContent/integrationTooltipContent';
import { useIntegrations } from '../../utils/useIntegrations/useIntegrations';
import { SettingsIntegrationsDocumentationButton } from '../documentationButton/documentationButton';

import styles from './integrations.module.scss';

export const Integrations = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const integrations = useIntegrations();

  const showFeedbackModal = useCallback(() => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'app:feedbackForm',
          arg: {
            team: 'partners',
            title: 'Feedback for conversation sidebar integrations',
          },
        },
      }),
    );
  }, [dispatch]);

  const navigateToPartnerCreate = (key: string, widgetType: string) => {
    ampli.partnerIntegrationsSettingsOverviewCreated({
      widgetType,
    });

    navigate({
      path: '/settings/integrations/partner-widget/:type/create',
      params: {
        path: {
          type: key,
        },
      },
    });
  };

  return (
    <Page.Section
      title="Create new integration"
      titleMeta={
        <>
          <SettingsIntegrationsDocumentationButton
            onClick={() =>
              elevioClient.openCategory(
                categories.settings.integrations.documentation,
              )
            }
          />
        </>
      }
    >
      <FlexBox gap="100" alignItems="center">
        <Text.Paragraph as="span" variant="4">
          Missing an integration?
        </Text.Paragraph>

        <Text.Paragraph as="span" variant="4" className={styles.externalLink}>
          <span onClick={() => showFeedbackModal()}>
            Talk to us about your use case
          </span>
        </Text.Paragraph>
      </FlexBox>

      <FlexBox
        gap="300"
        flexWrap="wrap"
        flexDirection="row"
        alignItems="start"
        className={styles.integrations}
      >
        {Object.entries(integrations)
          .filter(([key]) =>
            integrations[key].featureFlag
              ? Object.values(
                  featureFlags.get([integrations[key].featureFlag]),
                )[0]
              : true,
          )
          .map(
            ([key, { id, icon, label, iconColors, path, integrationType }]) => (
              <Tooltip
                key={id}
                content={getTooltipContent({
                  cardType: id,
                  integrationType,
                })}
              >
                <Box
                  className={styles.integrationsItem}
                  onClick={() =>
                    path
                      ? navigate({ path })
                      : navigateToPartnerCreate(key.toLowerCase(), id)
                  }
                >
                  <Avatar size="40" icon={icon} color={iconColors} />
                  <Text.Paragraph className={styles.integrationsItemLabel}>
                    {label}
                  </Text.Paragraph>
                </Box>
              </Tooltip>
            ),
          )}
      </FlexBox>
    </Page.Section>
  );
};
